import type { ClientEmailMatch } from "../../../slices/ContactsSlice";
import { isValidEmail } from "../../../utils/email";

const clean = (email: string | null | undefined) =>
  (email || "").trim().toLowerCase();

/**
 * The emails worth checking on Save (Roadmap 1784): valid, each once, and,
 * when editing, only the ones that weren't on the client before (so an old
 * shared address doesn't ask again on every save).
 */
export const emailsToCheck = (
  emails: (string | null | undefined)[],
  before: (string | null | undefined)[] = [],
): string[] => {
  const old = new Set(before.map(clean));
  const result: string[] = [];
  emails.forEach((email) => {
    const value = clean(email);
    if (!value || !isValidEmail(value)) return;
    if (old.has(value) || result.includes(value)) return;
    result.push(value);
  });
  return result;
};

/** "Jane Doe" / "Jane Doe (contact person of Acme Ltd)". */
export const matchOwner = (match: ClientEmailMatch) =>
  match.contactPersonName
    ? `${match.contactPersonName} (contact person of ${match.name})`
    : match.name;

/** Adds the closing period unless the text already ends with one ("Acme Inc."). */
const withPeriod = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);

/** The warning's sentence, e.g. "This email is already used by Jane Doe." */
export const duplicateEmailSentence = (matches: ClientEmailMatch[]) => {
  if (matches.length === 0) return "";
  const emails = Array.from(new Set(matches.map((match) => match.email)));
  const owners = Array.from(new Set(matches.map(matchOwner)));
  const subject = emails.length === 1 ? "This email is" : "These emails are";
  if (owners.length === 1)
    return withPeriod(`${subject} already used by ${owners[0]}`);
  if (owners.length === 2)
    return withPeriod(
      `${subject} already used by ${owners[0]} and ${owners[1]}`,
    );
  return `${subject} already used by ${owners[0]}, ${owners[1]} and ${
    owners.length - 2
  } more.`;
};
